import { Injectable, inject } from '@angular/core'
import * as toGeoJSON from '@tmcw/togeojson'
import { LoggerService } from '../../../services/logger.service'
import { errorDetails } from '../../../utils/error-details.util'
import { firstValueFrom, map, Observable, Subject } from 'rxjs'
import { read } from 'shapefile'
import { ShapeFiles } from '../interfaces/shapes-files.interface'
import { ZipEntry } from '../interfaces/zip-entry.interface'
import { ZipTaskProgress } from '../interfaces/zip-task-progress.interface'
import { ZipTask } from '../interfaces/zip-task.interface'

interface ZipJsEntry extends ZipEntry {
  getData(writer: object, onend: (blob: Blob) => void, onprogress: (current: number, total: number) => void): void
}

// This is added globally by the zip.js library
declare const zip: {
  workerScriptsPath: string
  BlobReader: new (blob: Blob) => object
  BlobWriter: new () => object
  createReader(
    reader: object,
    callback: (zipReader: { getEntries(callback: (entries: ZipJsEntry[]) => void): void }) => void,
    onerror: (message: unknown) => void,
  ): void
}

@Injectable({
  providedIn: 'root',
})
export class ImportService {
  private _logger = inject(LoggerService)

  constructor() {
    if (typeof zip !== 'undefined') {
      zip.workerScriptsPath = 'scripts/'
    } else {
      this._logger.warn('zip.js not loaded')
    }
  }

  public async filesToKmlOrShapefile(files: File[]): Promise<{ kml: File[]; shp: ShapeFiles[] }> {
    if (files?.length) {
      return {
        shp: await this.getShpDbfPairFiles(files),
        kml: this.getKmlFiles(files),
      }
    } else {
      throw new Error('No files provided')
    }
  }

  private getKmlFiles(files: File[]): File[] {
    return files.filter((f) => f.name.toLowerCase().endsWith('.kml'))
  }

  private async getShpDbfPairFiles(files: File[]): Promise<ShapeFiles[]> {
    let pairs: ShapeFiles[] = []
    if (files?.length) {
      // Categorize files
      const zipFiles: File[] = []
      const shpFiles: File[] = []
      const dbfFiles: File[] = []
      for (const file of files) {
        if (file.name.toLowerCase().endsWith('.shp')) {
          shpFiles.push(file)
        } else if (file.name.toLowerCase().endsWith('.dbf')) {
          dbfFiles.push(file)
        } else if (file.name.toLowerCase().endsWith('.zip')) {
          zipFiles.push(file)
        }
      }

      // Make file pairs with non-zip shp/dbf files
      for (const shpFile of shpFiles) {
        const dbfFile = dbfFiles.find((f) => f.name.slice(0, -4) === shpFile.name.slice(0, -4))
        pairs.push({ shp: shpFile, dbf: dbfFile })
      }

      // Extract zip files and add contained pairs
      for (const zipFile of zipFiles) {
        const unzippedFiles: File[] = await this.unzip(zipFile)
        const zipPairs: ShapeFiles[] = await this.getShpDbfPairFiles(unzippedFiles)
        pairs = pairs.concat(zipPairs)
      }
    }

    return pairs
  }

  private async unzip(file: File): Promise<File[]> {
    const data: File[] = []
    const zipEntries: ZipJsEntry[] = await firstValueFrom(this._getZipEntries(file))
    const zipTasks: {
      name: string
      lastModified: number
      task: ZipTask
    }[] = []

    for (const entry of zipEntries) {
      zipTasks.push({
        name: entry.filename,
        lastModified: new Date(entry.lastModDate).valueOf(),
        task: this._getZipData(entry),
      })
    }

    for (const task of zipTasks) {
      task?.task?.progress?.subscribe((progress) => this._logger.log('Unzip progress', progress))

      data.push(
        await firstValueFrom(
          task?.task?.data?.pipe(
            map((blob: Blob) => {
              // Transform Blob to File
              const f: Blob & { name?: string; lastModified?: number } = blob
              f.name = task.name
              f.lastModified = task.lastModified
              // a Blob given a name and a date, used where a File is expected
              return f as File
            }),
          ),
        ),
      )
    }

    return data
  }

  private _getZipEntries(file: File): Observable<Array<ZipJsEntry>> {
    return new Observable((subscriber) => {
      const reader = new zip.BlobReader(file)
      zip.createReader(
        reader,
        (zipReader) => {
          zipReader.getEntries((entries) => {
            subscriber.next(entries)
            subscriber.complete()
          })
        },
        (message: unknown) => {
          subscriber.error({ message })
        },
      )
    })
  }

  private _getZipData(entry: ZipJsEntry): ZipTask {
    const progress = new Subject<ZipTaskProgress | null>()
    const data: Observable<Blob> = new Observable<Blob>((subscriber) => {
      const writer = new zip.BlobWriter()
      entry.getData(
        writer,
        (blob: Blob) => {
          subscriber.next(blob)
          subscriber.complete()
          progress.next(null)
        },
        (current: number, total: number) => {
          progress.next({ active: true, current, total })
        },
      )
    })
    return { progress, data }
  }

  public mergeFeatures(
    featuresList: GeoJSON.FeatureCollection<GeoJSON.Geometry | null>[],
  ): GeoJSON.FeatureCollection<GeoJSON.Geometry | null> {
    const features = featuresList.reduce<GeoJSON.Feature<GeoJSON.Geometry | null>[]>((feats, fl) => {
      feats = feats.concat(fl.features)
      return feats
    }, [])
    return { type: 'FeatureCollection', features }
  }

  /**
  /**
   * Extracts a single consolidated feature collection from
   * a list of KML or Shapefile.
   */
  public async extract(...files: (File | ShapeFiles)[]): Promise<GeoJSON.FeatureCollection<GeoJSON.Geometry | null>> {
    const featureCollections: GeoJSON.FeatureCollection<GeoJSON.Geometry | null>[] = []

    for (const file of files) {
      if (file instanceof File) {
        try {
          featureCollections.push(await this.kmlToFeatures(file))
        } catch (err) {
          throw new Error(`Cannot read kml file - ${errorDetails(err).message}`, { cause: err })
        }
      } else {
        try {
          featureCollections.push(await read(file.shp.stream?.(), file.dbf?.stream?.()))
        } catch (err) {
          throw new Error(`Cannot read shapefile - ${errorDetails(err).message}`, { cause: err })
        }
      }
    }

    // Merge all the feature collections into a single one.
    const collection = this.mergeFeatures(featureCollections)
    return collection
  }

  public async kmlToFeatures(kmlFile: File): Promise<GeoJSON.FeatureCollection<GeoJSON.Geometry | null>> {
    const parser: DOMParser = new DOMParser()
    const xmlDoc: XMLDocument = parser.parseFromString(await kmlFile.text(), 'text/xml')
    return toGeoJSON.kml(xmlDoc)
  }
}
